import type { Message, MessagePayload } from 'pages/ws/components/chatSidebar/WsChatSidebar';

/**
 * コンテンツ本体（設問）が未生成かどうかを判定する。
 * 診断・クイズ・フォームいずれも新規作成の初期 state は空の設問を 1 行持つため、
 * 件数（0 件）だけでは判定できない。全設問の title が空（null・空文字含む）の場合も未生成とみなす。
 */
export function isContentNotGenerated(questions: { title?: string | null }[] | undefined): boolean {
  return (questions ?? []).length === 0 || (questions ?? []).every(q => !q.title);
}

/* ----------------------------------------------------------------------- */
/* 定数（動作仕様書 EV-content-chat-10 / 11） */
/* ----------------------------------------------------------------------- */

/** 「この内容で作成する」ボタン押下時に送る本文。R2 のトリガー表現と一致させる。 */
export const CREATE_COMMAND_PROMPT = 'この内容で作成して';

/** 「この内容で作成する」ボタンの表示文言。 */
export const CREATE_COMMAND_LABEL = 'この内容で作成する';

/** 「表紙の画像を作成する」ボタンの表示文言。 */
export const COVER_IMAGE_LABEL = '表紙の画像を作成する';

/** コンテンツ生成直後に表紙画像の作成を提案するメッセージ。 */
export const COVER_IMAGE_OFFER_MESSAGE =
  '表紙の画像もAIで作れるよ！下のボタンを押すと、タイトルと説明から画像を作るね。';

/** 表紙画像の生成が成功したときに表示するメッセージ。 */
export const COVER_IMAGE_DONE_MESSAGE =
  '表紙の画像を作ったよ！基本設定の「表紙の画像」を確認してね。';

/**
 * 表紙画像の生成に失敗したときのメッセージ。
 * 「しばらく時間をおいて」ではなく「選び直す」を先に出す理由: 29 秒タイムアウトで
 * 画像は生成済み・画像ライブラリには登録済みなのにクライアントはエラー扱いになるケースがあり、
 * 作り直しを促すと生成数を二重に消費させてしまうため。
 */
export const COVER_IMAGE_ERROR_MESSAGE =
  '申し訳ございません。表紙の画像の作成中にエラーが発生しました。基本設定の「表紙の画像」から画像を選び直すか、もう一度作成してみてください。';

/** 表紙画像ボタン押下時点で AI 画像生成数が上限に達していたときのメッセージ。 */
export const COVER_IMAGE_LIMIT_MESSAGE =
  'AI画像の生成数が上限に達しているため、表紙の画像を作れませんでした。';

/* ----------------------------------------------------------------------- */
/* 型定義 */
/* ----------------------------------------------------------------------- */

export type AiChatResponseShape = {
  answerContent: unknown;
  answerPatch: unknown;
  suggestions: unknown[] | null;
};

/* ----------------------------------------------------------------------- */
/* 判定関数 */
/* ----------------------------------------------------------------------- */

/**
 * trim 後に非空文字列かどうかを判定する（型述語）。
 * api 側 `_resolvePrompt` が trim 後に空判定するため、web の判定も trim 基準に揃える。
 * 空白のみのタイトルで「押せるボタン」を出し、api の 400 を踏むことを防ぐ。
 */
export function isFilled(value: string | undefined): value is string {
  return (value ?? '').trim() !== '';
}

/**
 * R5 / R9 の「実体なし応答」（相談・案内のみで生成もパッチも案も無い）かどうかを判定する。
 */
export function isNonGeneratingResponse(response: AiChatResponseShape): boolean {
  return (
    response.answerContent === null &&
    response.answerPatch === null &&
    (response.suggestions?.length ?? 0) === 0
  );
}

/**
 * 「この内容で作成する」ボタンを提示すべきかどうかを判定する。
 * ①実体なし応答 ②設問が未生成 ③title または description のどちらかが埋まっている、の AND。
 * ③を「どちらか」にする理由: API 側 R2 の発火条件（title または description が埋まっている）に合わせるため。
 * title だけを条件にすると、説明のみ入力済みの状態で R5 が存在しないボタンを案内してしまう。
 */
export function shouldOfferCreateCommand(params: {
  response: AiChatResponseShape;
  title: string | undefined;
  description: string | undefined;
  questions: { title?: string | null }[] | undefined;
}): boolean {
  return (
    isNonGeneratingResponse(params.response) &&
    isContentNotGenerated(params.questions) &&
    (isFilled(params.title) || isFilled(params.description))
  );
}

/**
 * 「表紙の画像を作成する」ボタンを提示すべきかどうかを判定する。
 * title を必須にする理由: `answerContent.title` が null のとき既存コードが
 * `setValue('title', undefined)` にするため、条件に入れないと必ず失敗するボタンを提示してしまう。
 */
export function shouldOfferCoverImage(params: {
  title: string | undefined;
  imageCountLimitReached: boolean;
}): boolean {
  return isFilled(params.title) && !params.imageCountLimitReached;
}

/**
 * AI 画像生成数が上限に達しているかどうかを判定する。無制限プランは常に false。
 */
export function isAiImageCountLimitReached(params: {
  aiImageUsageCount: number;
  aiImageCountLimit: number | undefined;
  noLimit: boolean;
}): boolean {
  if (params.noLimit) return false;
  return params.aiImageUsageCount >= (params.aiImageCountLimit ?? 0);
}

/**
 * one-shot ボタン（command / coverImage）を末尾に追加する。
 * 同種（`payload.type` が一致する）の既存ボタンは disabled にしてから追加するため、
 * 常に最新 1 件だけが押せる状態を保つ（二重生成防止）。
 */
export function appendOneShotOption(
  prev: Message[],
  payload: MessagePayload,
  label: string
): Message[] {
  const disabledPrev = prev.map(m =>
    m.payload?.type === payload.type ? { ...m, disabled: true } : m
  );
  return [...disabledPrev, { role: 'option', text: label, payload }];
}
